import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ShieldCheck,
  Stack,
  SealCheck,
  Camera,
  CaretDown,
  Lightbulb,
} from "@phosphor-icons/react";

const FACTS = [
  {
    icon: ShieldCheck,
    title: "Warum eine intakte Windschutzscheibe wichtig ist",
    text: (
      <>
        Die Windschutzscheibe dient nicht nur der freien Sicht. Bei vielen modernen Fahrzeugen ist sie{" "}
        <strong className="text-slate-900">in das Sicherheitskonzept der Karosserie eingebunden</strong>:
        Ihre fachgerechte Befestigung kann für die Stabilität der Fahrzeugstruktur und das Zusammenspiel
        verschiedener Sicherheitssysteme wichtig sein. Beschädigungen sollten deshalb frühzeitig geprüft
        werden — ob Reparatur oder Austausch, hängt von Art, Größe und Lage des Schadens ab.
      </>
    ),
  },
  {
    icon: Stack,
    title: "Verbundsicherheitsglas (VSG)",
    text: (
      <>
        Eine Frontscheibe besteht aus dem „Sandwich-System":{" "}
        <strong className="text-slate-900">Glas – PVB-Folie – Glas</strong>. Die extrem zähe Folie
        verhindert das Durchdringen von Gegenständen und hält Splitter bei Bruch zusammen — anders
        als das ESG (Einscheibensicherheitsglas) der Seitenscheiben, das kontrolliert zerbröselt.
      </>
    ),
  },
  {
    icon: SealCheck,
    title: "Erstausrüsterqualität (OEM)",
    text: (
      <>
        Jede Scheibe hat eine spezifische <strong className="text-slate-900">OE-Nummer</strong> für
        exakte Passgenauigkeit. Erstausrüster wie Saint-Gobain Sekurit, Pilkington, AGC und FUYAO
        fertigen Scheiben, die technisch exakt dem Originalteil der Fahrzeughersteller entsprechen.
      </>
    ),
  },
  {
    icon: Camera,
    title: "Kamera & Kalibrierung (ADAS)",
    text: (
      <>
        Moderne Windschutzscheiben sind Halterung für Kameras und Sensoren von Spurhalte- und
        Notbremsassistent. Nach einem Tausch kann je nach Fahrzeug und{" "}
        <strong className="text-slate-900">Herstellervorgaben eine Überprüfung oder Kalibrierung</strong>{" "}
        erforderlich sein — dazu kommen Komfort-Features wie Akustikfolien und IR-Thermoschutz.
      </>
    ),
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "Welche Rolle spielt die Frontscheibe für die Fahrzeugsicherheit?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Die Windschutzscheibe dient nicht nur der freien Sicht: Bei vielen modernen Fahrzeugen ist sie in das Sicherheitskonzept der Karosserie eingebunden. Ihre fachgerechte Befestigung kann für die Stabilität der Fahrzeugstruktur und das Zusammenspiel verschiedener Sicherheitssysteme wichtig sein. Beschädigungen sollten deshalb frühzeitig geprüft werden.",
      },
    },
    {
      "@type": "Question",
      name: "Was ist Verbundsicherheitsglas (VSG)?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Verbundsicherheitsglas besteht aus zwei Glasscheiben, die durch eine reißfeste PVB-Folie miteinander verklebt sind. Es verhindert das Durchschlagen von Gegenständen und hält Glassplitter bei Beschädigung zusammen — im Unterschied zum ESG (Einscheibensicherheitsglas) der Seitenscheiben.",
      },
    },
    {
      "@type": "Question",
      name: "Was bedeutet Erstausrüsterqualität (OEM) bei Autoglas?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Scheiben in Erstausrüsterqualität stammen von Herstellern wie Saint-Gobain Sekurit, Pilkington, AGC oder FUYAO und entsprechen bezüglich Sicherheit, Passgenauigkeit und OE-Nummer exakt den Spezifikationen der Fahrzeughersteller.",
      },
    },
    {
      "@type": "Question",
      name: "Muss nach jedem Windschutzscheibentausch eine Kamera kalibriert werden?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Nicht jedes Fahrzeug verfügt über die gleichen Fahrerassistenzsysteme. Ob nach einem Scheibentausch eine Kalibrierung erforderlich ist, hängt von der verbauten Technik und den Herstellervorgaben ab. Erforderliche Prüf- und Kalibrierarbeiten sollten fachgerecht durchgeführt werden.",
      },
    },
  ],
};

export const AutoglasFacts = () => {
  const [open, setOpen] = useState(false);

  return (
    <section id="autoglas-fakten" className="py-16 md:py-20 bg-slate-50 border-t border-slate-200">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest">
            <Lightbulb size={14} weight="fill" />
            Wussten Sie schon?
          </span>
          <h2 className="mt-4 text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
            Wichtige Fakten über Ihr Autoglas — Sicherheit, VSG &amp; ADAS
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Mehr als nur Windschutz: Ihre Frontscheibe ist ein Hightech-Bauteil für Sicherheit,
            Fahrzeugstruktur und Fahrassistenzsysteme.
          </p>
          <button
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="autoglas-fakten-inhalt"
            className="mt-6 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-colors"
            data-testid="autoglas-facts-toggle"
          >
            {open ? "Fakten einklappen" : "Jetzt mehr über Autoglas lernen"}
            <CaretDown
              size={18}
              weight="bold"
              className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        {/* Inhalte bleiben im DOM (SEO) — nur visuell ein-/ausgeblendet */}
        <div
          id="autoglas-fakten-inhalt"
          className={open ? "block animate-in fade-in slide-in-from-top-4 duration-500" : "hidden"}
          data-testid="autoglas-facts-content"
        >
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="autoglas-facts-grid">
            {FACTS.map(({ icon: Icon, title, text }) => (
              <article
                key={title}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col items-start transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-blue-200"
              >
                <div className="w-14 h-14 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5">
                  <Icon size={30} weight="duotone" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2.5">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
              </article>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-slate-600">
            Vertiefen Sie Ihr Wissen:{" "}
            <Link to="/leistung/windschutzscheibe-tauschen" className="font-semibold text-blue-600 hover:text-blue-700 underline underline-offset-2">
              Windschutzscheibe wechseln
            </Link>
            {" · "}
            <Link to="/leistung/steinschlagreparatur" className="font-semibold text-blue-600 hover:text-blue-700 underline underline-offset-2">
              Steinschlagreparatur
            </Link>
            {" · "}
            <Link to="/leistung/adas-kalibrierung" className="font-semibold text-blue-600 hover:text-blue-700 underline underline-offset-2">
              ADAS-Kalibrierung
            </Link>
            {" · "}
            <Link to="/faq" className="font-semibold text-blue-600 hover:text-blue-700 underline underline-offset-2">
              Alle FAQ
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
};

export default AutoglasFacts;
